import React from 'react';
import { Linking, StyleSheet } from 'react-native';
import { DNABox, DNAText } from '@alucio/lux-ui';
import ReactMarkdown, { Components } from 'react-markdown';

type DNATextChildren = React.ComponentProps<typeof DNAText>['children'];

const asDNATextChildren = (children: React.ReactNode): DNATextChildren => children as DNATextChildren;

const styles = StyleSheet.create({
  heading: {
    marginBottom: 8,
  },
  paragraph: {
    marginBottom: 8,
  },
  list: {
    marginBottom: 8,
  },
});

const markdownComponents: Components = {
  h5: ({ children }) => <DNAText b2 bold style={styles.heading}>{asDNATextChildren(children)}</DNAText>,
  h6: ({ children }) => <DNAText b2 bold style={styles.heading}>{asDNATextChildren(children)}</DNAText>,
  a: ({ children, href }) => (
    <DNAText
      status="primary"
      accessibilityRole="link"
      onPress={href && /^(https?:|mailto:)/i.test(href) ? () => Linking.openURL(href) : undefined}
    >
      {asDNATextChildren(children)}
    </DNAText>
  ),
  code: ({ children }) => <DNAText status="tertiary">{asDNATextChildren(children)}</DNAText>,
  pre: ({ children }) => <DNABox appearance="col" style={styles.paragraph}>{children}</DNABox>,
  blockquote: ({ children }) => <DNABox appearance="col" style={styles.paragraph}>{children}</DNABox>,
  hr: () => <DNAText status="subtle">—</DNAText>,
  br: () => <DNAText>{'\n'}</DNAText>,
  img: ({ alt }) => <DNAText status="tertiary">{alt || ''}</DNAText>,
  h1: ({ children }) => <DNAText h4 bold style={styles.heading}>{asDNATextChildren(children)}</DNAText>,
  h2: ({ children }) => <DNAText h5 bold style={styles.heading}>{asDNATextChildren(children)}</DNAText>,
  h3: ({ children }) => <DNAText b1 bold style={styles.heading}>{asDNATextChildren(children)}</DNAText>,
  h4: ({ children }) => <DNAText b2 bold style={styles.heading}>{asDNATextChildren(children)}</DNAText>,
  p: ({ children }) => <DNAText status="tertiary" style={styles.paragraph}>{asDNATextChildren(children)}</DNAText>,
  strong: ({ children }) => <DNAText status="tertiary" bold>{asDNATextChildren(children)}</DNAText>,
  em: ({ children }) => <DNAText status="tertiary" italic>{asDNATextChildren(children)}</DNAText>,
  ul: ({ children }) => <DNABox appearance="col" spacing="xs" style={styles.list}>{children}</DNABox>,
  ol: ({ children }) => <DNABox appearance="col" spacing="xs" style={styles.list}>{children}</DNABox>,
  li: ({ children }) => (
    <DNABox spacing="xs">
      <DNAText status="tertiary">•</DNAText>
      <DNABox fill>{children}</DNABox>
    </DNABox>
  ),
};

interface DNADocumentSummaryContentProps {
  summary: string;
}

const DNADocumentSummaryContent: React.FC<DNADocumentSummaryContentProps> = ({ summary }) => (
  <ReactMarkdown
    components={markdownComponents}
    allowedElements={Object.keys(markdownComponents)}
    unwrapDisallowed
    skipHtml
  >
    {summary}
  </ReactMarkdown>
);

export default DNADocumentSummaryContent;
